import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ShieldCheck } from "lucide-react";
import { scrollToHash } from "./Header";

const HERO_IMG =
  "https://images.unsplash.com/photo-1642653304692-cb35d5c0fc57?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINES = ["THE WORLD'S FIRST", "COMFORT-ZONE", "FINDER."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } },
};

const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const dialRotate = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-[var(--sp-line)] pt-16"
    >
      <motion.div
        aria-hidden="true"
        style={{ rotate: dialRotate }}
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full border border-dashed border-[var(--sp-line)]"
      >
        <span className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 bg-[var(--sun)]" />
      </motion.div>
      <motion.div
        aria-hidden="true"
        style={{ rotate: dialRotate }}
        className="pointer-events-none absolute -left-64 bottom-0 h-[420px] w-[420px] rounded-full border border-[var(--sp-line)] opacity-60"
      />

      <div className="relative mx-auto grid max-w-[1500px] grid-cols-1 gap-12 px-5 pb-16 pt-16 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            data-testid="hero-eyebrow"
            className="mb-8 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
          >
            <span className="h-px w-10 bg-[var(--sun)]" />
            Mission // Solar Comfort Intelligence
          </motion.p>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            data-testid="hero-headline"
            className="font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-6xl lg:text-7xl"
          >
            {LINES.map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span
                  variants={lineReveal}
                  className={`block ${i === 1 ? "text-[var(--sun)]" : ""} ${i === 2 ? "text-outline" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-subcopy"
            className="mt-8 max-w-xl text-base leading-relaxed text-[var(--sp-muted)] sm:text-lg"
          >
            ShadyParking.Space computes live sun position against real-world structures to locate
            precision comfort — the shaded parking bay, the cool campsite canopy, the exact patch of
            sun worth lying in. Thermal certainty, on demand.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#simulator"
              onClick={(e) => scrollToHash(e, "#simulator")}
              data-testid="hero-find-shade-button"
              className="group inline-flex items-center gap-3 bg-[var(--sun)] px-7 py-4 font-mono2 text-xs font-bold uppercase tracking-[0.16em] text-black transition-transform duration-300 hover:-translate-y-0.5"
            >
              Run the Simulator
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <a
              href="#growth-path"
              onClick={(e) => scrollToHash(e, "#growth-path")}
              data-testid="hero-growth-path-button"
              className="inline-flex items-center gap-3 border border-[var(--sp-line)] px-7 py-4 font-mono2 text-xs uppercase tracking-[0.16em] text-[var(--sp-fg)] transition-colors duration-300 hover:border-[var(--sun)] hover:text-[var(--sun)]"
            >
              The Growth Path
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            data-testid="hero-privacy-strip"
            className="mt-10 flex items-center gap-3 text-[var(--sp-muted)]"
          >
            <ShieldCheck className="h-4 w-4 text-[var(--sun)]" />
            <span className="font-mono2 text-[11px] uppercase tracking-[0.16em]">
              No account. No tracking. Your privacy is preserved.
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div className="relative border border-[var(--sp-line)]" data-testid="hero-image-frame">
              <span className="absolute -left-1.5 -top-1.5 z-10 h-3 w-3 border-l-2 border-t-2 border-[var(--sun)]" />
              <span className="absolute -right-1.5 -top-1.5 z-10 h-3 w-3 border-r-2 border-t-2 border-[var(--sun)]" />
              <span className="absolute -bottom-1.5 -left-1.5 z-10 h-3 w-3 border-b-2 border-l-2 border-[var(--sun)]" />
              <span className="absolute -bottom-1.5 -right-1.5 z-10 h-3 w-3 border-b-2 border-r-2 border-[var(--sun)]" />
              <div className="overflow-hidden">
                <motion.img
                  src={HERO_IMG}
                  alt="Architecture casting hard geometric shadows"
                  style={{ y: imgY, scale: 1.15 }}
                  className="aspect-[4/5] w-full object-cover grayscale contrast-125"
                />
              </div>
              <div className="absolute inset-0 bg-[var(--sun)] mix-blend-multiply opacity-15" />
              <div className="absolute bottom-0 left-0 flex items-center gap-2 bg-[var(--sun)] px-3 py-2">
                <span className="sp-blink h-1.5 w-1.5 bg-black" />
                <span className="font-mono2 text-[10px] font-bold uppercase tracking-[0.14em] text-black">
                  Shade Detected
                </span>
              </div>
            </div>
            <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-faint)]">
              Sample readout · illustrative values
            </p>
            <div
              data-testid="hero-telemetry"
              className="mt-2 grid grid-cols-3 divide-x divide-[var(--sp-line)] border border-[var(--sp-line)] bg-[var(--sp-surface)]"
            >
              {[
                ["SOLAR ELEV", "41.7°"],
                ["AZIMUTH", "212°"],
                ["SHADOW IDX", "0.83"],
              ].map(([k, v]) => (
                <div key={k} className="px-4 py-3">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.1em] text-[var(--sp-faint)]">{k}</p>
                  <p className="mt-1 font-mono2 text-sm font-bold text-[var(--sun)]">{v}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
